import { Core } from "@faire/design-tokens";
import {
  clearSelectedBrandOrderLocalStorage,
  setSelectedBrandOrderToLocalStorage,
} from "@faire/help-center-self-serve/utils/localStorage";
import { getBrandToken } from "@faire/help-center-utils/serialized-data/getBrand";
import {
  getRetailerToken,
  isRetailer,
} from "@faire/help-center-utils/serialized-data/getRetailer";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Column, Flex, Grid } from "@faire/web--source/slate/Layout";
import { SearchInput } from "@faire/web--source/slate/SearchInput";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDebouncedValue } from "@faire/web--source/ui/hooks/useDebouncedValue";
import { useState } from "@faire/web--source/ui/hooks/useState";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackCxFoundationsOrderListPageView } from "@faire/web-api--source/events/cxFoundations/view/orderListPage";
import { trackHelpCenterOrderSkipClick } from "@faire/web-api--source/events/helpCenter/click/orderSkip";
import { trackHelpCenterSelfServeOrderSearchKeyboardInput } from "@faire/web-api--source/events/helpCenter/keyboardInput/selfServeOrderSearch";
import { ISelfServeBrandOrder } from "@faire/web-api--source/indigofair/helpcenter/ISelfServeBrandOrder";
import React, { useEffect, useRef } from "react";
import styled from "styled-components";

import { BackButton } from "../../components/BackButton";
import { OrderItemCard } from "../../components/OrderItemCard";
import { useSelfServeContextStore } from "../../hooks/SelfServeContext";
import { useIssueType } from "../../hooks/useIssueType";
import { useNavigation } from "../../hooks/useNavigation";
import { useOrderItemCardGrid } from "../../hooks/useOrderItemCardGrid";
import { useSearchPaginatedBrandOrders } from "../../hooks/useSearchPaginatedBrandOrders";
import { useTrackPageViewEffect } from "../../hooks/useTrackPageViewEffect";
import { SearchResultNotFound } from "../components/SearchResultNotFound";

import { BrandOrdersLoadingSkeleton } from "./BrandOrdersLoadingSkeleton";
import { NotRelatedToOrderButtonConditionally } from "./NotRelatedToOrderButton";
import { ShowMoreOrdersButton } from "./ShowMoreOrdersButton";
import { SkipButtonConditionally } from "./SkipButton";
import { trackClickOnIssueUnrelatedToOrder } from "./utils";

const SEARCH_DEBOUNCE_TIME = 300;

export const Order: React.FC = () => {
  const { goToNextStep } = useNavigation();
  const [, setSelfServeContextStore] = useSelfServeContextStore([]);
  const [searchText, setSearchText] = useState<string>("");
  const [focused, setFocused] = useState<boolean>(false);
  const [isUserFirstType, setIsUserFirstType] = useState<boolean>(true);

  const debouncedSearchValue = useDebouncedValue(
    searchText,
    SEARCH_DEBOUNCE_TIME
  );
  const {
    brandOrders = [],
    isLoading,
    showMoreOrders,
    hasMoreOrders,
  } = useSearchPaginatedBrandOrders({ query: debouncedSearchValue });
  useTrackPageViewEffect(trackCxFoundationsOrderListPageView);
  const retailerToken = getRetailerToken() ?? "";
  const brandToken = getBrandToken() ?? "";
  const { issueType } = useIssueType();
  const { isMobile } = useViewport();
  const { strictLocalize } = useStrictLocalization();

  const brandOrdersRef = useRef<HTMLDivElement | null>(null);
  const orderItemCardRef = useRef<HTMLDivElement | null>(null);

  const { gap, itemsPerRow } = useOrderItemCardGrid();

  useEffect(() => {
    if (searchText !== "" && isUserFirstType) {
      trackHelpCenterSelfServeOrderSearchKeyboardInput(
        retailerToken,
        brandToken,
        searchText,
        "true"
      );
      setIsUserFirstType(false);
    }
  }, [
    searchText,
    setIsUserFirstType,
    isUserFirstType,
    retailerToken,
    brandToken,
  ]);

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchText(event.target.value);
  };

  const handleIssueUnrelatedButtonClick = () => {
    trackClickOnIssueUnrelatedToOrder();
    clearSelectedBrandOrderLocalStorage();
    setSelfServeContextStore({ selectedBrandOrder: undefined });
    goToNextStep();
  };

  const handleSkipButtonClick = () => {
    trackHelpCenterOrderSkipClick(retailerToken, brandToken, "", issueType);
    clearSelectedBrandOrderLocalStorage();
    setSelfServeContextStore({ selectedBrandOrder: undefined });
    goToNextStep();
  };

  const handleOrderItemClick = (brandOrder: ISelfServeBrandOrder) => {
    setSelfServeContextStore({ selectedBrandOrder: brandOrder });
    setSelectedBrandOrderToLocalStorage(brandOrder);
    goToNextStep({ brandOrder });
  };

  return (
    <Wrapper data-test-id="order-select-wrapper">
      <BackButton />
      {isMobile ? null : <Spacer height="3x" />}
      <HeadingWrapper>
        <Typography variant="sectionHeaderSansMedium">
          <StrictLocalMsg
            defaultMessage="Is this related to a specific order?"
            description={{
              text: "This is the heading for the order select page. It asks the user if the issue they are experiencing is related to a specific order.",
            }}
          />
        </Typography>
        <SkipButtonConditionally onClick={handleSkipButtonClick} />
      </HeadingWrapper>
      <Spacer height="3x" />
      <OrderSearchInput
        width="100%"
        value={searchText}
        onChange={handleSearchInputChange}
        onClear={() => setSearchText("")}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={
          isRetailer()
            ? strictLocalize({
                defaultMessage: "Search by brand or order number",
                description: {
                  text: "This is the placeholder text for the search input on the order select page.",
                },
              })
            : strictLocalize({
                defaultMessage: "Search by customer or order number",
                description: {
                  text: "This is the placeholder text for the search input on the order select page.",
                },
              })
        }
      />
      <Spacer height="2x" />
      <Grid
        ref={brandOrdersRef}
        gap={gap}
        columns={itemsPerRow}
        style={{ width: "100%" }}
        columnTemplate={(columns: number) =>
          isMobile
            ? `repeat(${columns}, minmax(150px, 1fr))`
            : `repeat(${columns}, 1fr)`
        }
      >
        {brandOrders.map((brandOrder, index) => {
          return (
            <OrderItemCard
              innerRef={orderItemCardRef}
              key={index}
              {...brandOrder}
              isRetailer={isRetailer()}
              onClick={() => handleOrderItemClick(brandOrder)}
            />
          );
        })}
        <NotRelatedToOrderButtonConditionally
          brandOrders={brandOrders}
          focused={focused}
          searchText={searchText}
          onClick={handleIssueUnrelatedButtonClick}
        />
      </Grid>
      {brandOrders.length === 0 && !isLoading ? (
        <SearchResultNotFound
          width="100%"
          searchWord={searchText}
          clearSearch={() => setSearchText("")}
        />
      ) : null}
      <BrandOrdersLoadingSkeleton isLoading={isLoading} />
      <Spacer height="3x" />
      <ShowMoreOrdersButton
        visibleWhen={hasMoreOrders()}
        onClick={showMoreOrders}
      />
    </Wrapper>
  );
};

const HeadingWrapper = styled(Flex).attrs({
  direction: { tabletAndAbove: "row", mobileAndAbove: "column" },
  justify: { tabletAndAbove: "space-between", mobileAndAbove: "flex-start" },
  align: "center",
})`
  width: 100%;
`;

const Wrapper = styled(Column).attrs({ align: "flex-start" })`
  background-color: ${Core.surface.inverse};
  padding: ${getSpacing("4x", "3x", "4x", "5x")};
  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("2x")};
  }
`;

const OrderSearchInput = styled(SearchInput)<{ width?: string }>`
  width: ${(props) => props.width ?? "100%"};
  ${responsiveMediaQueries.mobile} {
    width: 100%;
  }
`;
